import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { backend } from "Backend";
import DebouncedText from "components/DebouncedText";
import {
  CustomDocumentProperty,
  CustomPropertyCategory,
  CustomPropertyNameMatcherInput,
  CustomPropertyType,
  CustomPropertyVisibility,
  DeletePropSelectOptionsDocument,
  MigrateCustomPropertyTypeDocument,
  UpsertDocumentPropertyMutationVariables,
} from "generated/graphql";
import { distinct } from "helpers/coll";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { FC, useEffect, useState } from "react";
import { upsertDocumentProperty } from "redux/slices/lensEditor";
import { P } from "theme/typography";
import { Add, Edit, Remove } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import CustomPropertyCell from "components/CustomPropertyCell";
import RainbowText from "components/RainbowText";
import { CustomPropertySchema } from "helpers/customProperties";
import SelectOptions from "components/SelectOptions/SelectOptions";
import PropertySelectOptionDialog from "routes/lens-editor/profile-builder/PropertySelectOptionDialog";
import { useSnackbar } from "notistack";
import { runGqlMutation } from "gqlClient";

interface NameMatcherUI extends CustomPropertyNameMatcherInput {
  id: number;
}

interface UpsertDocumentPropertyMutationVariablesCorrect extends UpsertDocumentPropertyMutationVariables {
  selectOptions: string[];
}

const NameMatcher: FC<{
  property: CustomPropertySchema;
  nameMatcher: NameMatcherUI;
  regexTest: string;
  setNameMatcher: (matcher: NameMatcherUI) => void;
  remove: (matcher: NameMatcherUI) => void;
}> = ({ property, nameMatcher, regexTest, setNameMatcher, remove }) => {
  const [matches, setMatches] = useState<{ start: number; end: number; substring: string }[]>([]);

  const fetchFirstMatch = (pattern: string) => {
    if (regexTest) {
      backend
        .testRegex(pattern, regexTest)
        .then((match) => {
          setMatches(match ? [match] : []);
        })
        .catch((error) => {
          console.error("CustomPropertyDialog; Error testing regex", error);
        });
    }
  };

  const updateNameMatcherValue = (value) => {
    setNameMatcher({
      ...nameMatcher,
      value: value,
    });
  };

  const updateNameMatcherRegex = (evt) => {
    setNameMatcher({
      ...nameMatcher,
      regex: evt.target.value,
    });
    fetchFirstMatch(evt.target.value);
  };

  useEffect(() => {
    fetchFirstMatch(nameMatcher.regex);
  }, [regexTest]);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <P variant="smSemiBold" sx={{ color: "grey.400", my: 1, display: "block" }}>
          Matcher
        </P>
        <IconButton onClick={() => remove(nameMatcher)}>
          <Remove />
        </IconButton>
      </Box>
      <FormControl fullWidth required sx={{ mb: "0!important" }}>
        <TextField value={nameMatcher.regex} onChange={updateNameMatcherRegex} label="Regex" />
      </FormControl>
      <FormControl fullWidth required sx={{ mb: "0!important" }}>
        <CustomPropertyCell customProperty={property} value={nameMatcher.value} setValue={updateNameMatcherValue} />
      </FormControl>
      <RainbowText
        sx={{
          fontFamily: "Inter",
        }}
        text={regexTest}
        annotations={matches.map((m) => ({
          start: m.start,
          end: m.end,
          highlight: true,
          tag: "",
          color: "yellow",
        }))}
      />
    </>
  );
};

interface Props {
  open: boolean;
  close: () => void;
  customProp?: CustomDocumentProperty;
  lensId: number;
}
const DocumentPropertyDialog: FC<Props> = ({ open, close, customProp, lensId }) => {
  const [property, setProperty] = useState<CustomDocumentProperty | undefined>(customProp);
  const [key, setKey] = useState<string>(property?.key || "");
  const [displayName, setDisplayName] = useState<string>(property?.displayName || "");
  const [isHidden, setIsHidden] = useState<boolean>(property?.isHidden ?? false);
  const [dataType, setDataType] = useState<CustomPropertyType | undefined>(property?.dataType);
  const [collectVisibility, setCollectVisibility] = useState<CustomPropertyVisibility>(
    property?.collectVisibility || CustomPropertyVisibility.Hidden
  );
  const [selectOptions, setSelectOptions] = useState<string[]>(property?.selectOptions || []);
  const [preventsLensHits, setPreventsLensHits] = useState<boolean>(
    (property as CustomDocumentProperty)?.preventsLensHits || false
  );
  const [regexTest, setRegexTest] = useState("");
  const [nameMatchers, setNameMatchers] = useState<NameMatcherUI[]>(
    (property as CustomDocumentProperty)?.nameMatchers?.map((nm) => ({ ...nm, id: uuidv4() })) || []
  );
  const snackpatch = useAppDispatchWithSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const canSave = key.length > 0 && displayName.length > 0 && dataType;
  const [showMigrateSelectOptionDialog, setMigrateSelectOptionDialog] = useState(false);
  const deletedSelectOptions = (property?.selectOptions || []).filter((o) => !selectOptions.includes(o));

  const updateNameMatchers = (nameMatcher: NameMatcherUI): void => {
    if (nameMatchers) {
      const foundMatcherIndex = nameMatchers.findIndex((nm) => nm.id === nameMatcher.id);

      if (foundMatcherIndex > -1) {
        const nameMatchersCopy = nameMatchers.slice();
        nameMatchersCopy.splice(foundMatcherIndex, 1, nameMatcher);
        setNameMatchers(nameMatchersCopy);
      }
    }
  };

  const removeNameMatcher = (nameMatcher: NameMatcherUI): void => {
    if (nameMatchers) {
      const foundMatcherIndex = nameMatchers.findIndex((nm) => nm.id === nameMatcher.id);

      if (foundMatcherIndex > -1) {
        const nameMatchersCopy = nameMatchers.slice();
        nameMatchersCopy.splice(foundMatcherIndex, 1);
        setNameMatchers(nameMatchersCopy);
      }
    }
  };

  const currentProperty: UpsertDocumentPropertyMutationVariablesCorrect = {
    key,
    displayName,
    lensId,
    isHidden,
    dataType: dataType || CustomPropertyType.Text,
    collectVisibility,
    selectOptions,
    preventsLensHits,
    nameMatchers: nameMatchers.map((nm) => ({
      regex: nm.regex,
      value: nm.value,
    })),
  };

  const save = async () => {
    if (preventsLensHits !== undefined && nameMatchers !== undefined && dataType !== undefined) {
      const migratePropOrDeleteSelectOptionFunc = async () => {
        const oldDataType = property?.dataType;
        if (oldDataType !== undefined && oldDataType !== dataType) {
          return runGqlMutation(MigrateCustomPropertyTypeDocument, {
            lensId,
            key,
            dataType,
            propCategory: CustomPropertyCategory.DocumentProperty,
          });
        } else if (deletedSelectOptions.length > 0) {
          return runGqlMutation(DeletePropSelectOptionsDocument, {
            lensId,
            key,
            selectOptionsToDelete: deletedSelectOptions,
            propCategory: CustomPropertyCategory.DocumentProperty,
          });
        } else {
          return Promise.resolve();
        }
      };
      await migratePropOrDeleteSelectOptionFunc()
        .then(() =>
          snackpatch(upsertDocumentProperty(currentProperty), {
            successMessage: "Document Property Success",
            errorMessage: "Document Property Error",
          })
        )
        .catch(() =>
          enqueueSnackbar(`Failed to update Document Property`, {
            variant: "error",
          })
        );
    }

    close();
  };

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>{property ? "Update" : "New"} Property</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 2,
            " .MuiFormControl-root": {
              mb: 2,
            },
          }}>
          <FormControl fullWidth required>
            <DebouncedText
              value={key}
              textFieldProps={{
                disabled: !!property,
                helperText: "No spaces",
              }}
              onChange={(key) => {
                setKey(key);
              }}
              formatter={(text) => text.toLowerCase().replace(/\s/g, "")}
              delayMs={1}
              label="Key"
            />
          </FormControl>
          <FormControl fullWidth required>
            <TextField
              value={displayName}
              onChange={(evt) => {
                setDisplayName(evt.target.value);
              }}
              label="Display Name"
            />
          </FormControl>
          <FormControl fullWidth disabled={dataType === CustomPropertyType.Json}>
            <FormLabel>Is Hidden</FormLabel>
            <RadioGroup row value={isHidden} onChange={(e) => setIsHidden(e.target.value === "true" ? true : false)}>
              <FormControlLabel value={false} control={<Radio />} label="No" />
              <FormControlLabel value={true} control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Data Type</InputLabel>
            <Select
              disabled={!!property && property.dataType === CustomPropertyType.Text}
              label="Data Type"
              value={dataType}
              onChange={(e) => {
                const dt = e.target.value as CustomPropertyType;
                setDataType(dt);
                if (dt !== CustomPropertyType.Select) {
                  setSelectOptions([]);
                }
                if (dt === CustomPropertyType.Json) {
                  setIsHidden(true);
                }
              }}>
              <MenuItem value={CustomPropertyType.Text}>Text</MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Date
                }
                value={CustomPropertyType.Date}>
                Date
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Number
                }
                value={CustomPropertyType.Number}>
                Number
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Select
                }
                value={CustomPropertyType.Select}>
                Select
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Json
                }
                value={CustomPropertyType.Json}>
                JSON
              </MenuItem>
            </Select>
          </FormControl>
          {dataType === CustomPropertyType.Select && (
            <div className="flex mx-4">
              {showMigrateSelectOptionDialog && (
                <PropertySelectOptionDialog
                  onClose={() => setMigrateSelectOptionDialog(false)}
                  availableSelectOptions={selectOptions}
                  onSuccess={(oldValue: string, newValue: string) => {
                    setMigrateSelectOptionDialog(false);
                    const newSelectOptions = selectOptions.map((o) => (o === oldValue ? newValue : o));
                    setSelectOptions(newSelectOptions);
                    if (property !== undefined) {
                      setProperty({ ...property, selectOptions: newSelectOptions });
                    }
                  }}
                  propertyType={CustomPropertyCategory.DocumentProperty}
                  propertyKey={key}
                />
              )}
              {property !== undefined && (
                <div>
                  <Tooltip title="Rename Option" sx={{ mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMigrateSelectOptionDialog(true);
                      }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              <SelectOptions
                options={selectOptions}
                setOptions={(options) => {
                  const distinctValues = distinct(options);
                  setSelectOptions(distinctValues);
                }}
              />
            </div>
          )}
          <FormControl fullWidth>
            <InputLabel>Collect Visibility</InputLabel>
            <Select
              label="Collect Visibility"
              value={collectVisibility}
              onChange={(e) => {
                const cv = e.target.value as CustomPropertyVisibility;
                setCollectVisibility(cv);
              }}>
              <MenuItem value={CustomPropertyVisibility.Hidden}>Hidden</MenuItem>
              <MenuItem value={CustomPropertyVisibility.Visible}>Visible</MenuItem>
              <MenuItem value={CustomPropertyVisibility.Editable}>Editable</MenuItem>
              <MenuItem value={CustomPropertyVisibility.Required}>Required</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>Prevents Lens Hits</FormLabel>
            <RadioGroup
              row
              value={preventsLensHits}
              onChange={(e) => setPreventsLensHits(e.target.value === "true" ? true : false)}>
              <FormControlLabel value={false} control={<Radio />} label="No" />
              <FormControlLabel value={true} control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <P variant="baseSemiBold">Document Name Matchers</P>
            <IconButton
              onClick={() =>
                setNameMatchers((prev) => {
                  if (prev !== undefined) {
                    const newPrev = prev.slice();
                    newPrev.push({
                      id: uuidv4(),
                      regex: "",
                      value: "",
                    });
                    return newPrev;
                  } else {
                    return [
                      {
                        id: uuidv4(),
                        regex: "",
                        value: "",
                      },
                    ];
                  }
                })
              }>
              <Add />
            </IconButton>
          </Box>
          <FormControl fullWidth required sx={{ mb: "0!important" }}>
            <TextField
              value={regexTest}
              onChange={(evt) => {
                setRegexTest(evt.target.value);
              }}
              label="Test Document Name"
            />
          </FormControl>
          {nameMatchers &&
            nameMatchers.map((nameMatcher) => (
              <NameMatcher
                key={nameMatcher.id}
                property={currentProperty}
                nameMatcher={nameMatcher}
                regexTest={regexTest}
                setNameMatcher={updateNameMatchers}
                remove={removeNameMatcher}
              />
            ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={close}>
          Cancel
        </Button>
        <Button disabled={!canSave} color="primary" sx={{ ml: 2 }} onClick={save} variant="contained">
          {!!property && property?.dataType !== dataType
            ? "Migrate and "
            : deletedSelectOptions.length > 0
              ? "Delete select options and "
              : ""}
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPropertyDialog;
